/**
 * Download Utilities
 *
 * Functions for downloading images with proper filenames including attempt numbers.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Include attempt number in filename
 * - User-friendly download experience
 */

import { ServiceType } from '@/shared/types';
import { getServiceName } from './formatting';

/**
 * Download image from URL with custom filename
 *
 * @param url - Image URL (Firebase download token URL)
 * @param fileName - Desired filename
 */
export async function downloadImage(url: string, fileName: string): Promise<void> {
  try {
    // Fetch the image
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to download image');
    }

    // Get the blob
    const blob = await response.blob();

    // Create download link
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download error:', error);
    throw new Error('Failed to download image. Please try again.');
  }
}

/**
 * Generate filename for order download
 *
 * @param serviceType - Service type
 * @param attemptNumber - Current attempt number
 * @param originalFileName - Original uploaded filename (optional)
 * @returns Generated filename
 */
export function generateDownloadFileName(
  serviceType: ServiceType,
  attemptNumber: number,
  originalFileName?: string
): string {
  // Get service name in lowercase with hyphens
  const serviceName = getServiceName(serviceType).toLowerCase().replace(/\s+/g, '-');

  // Extract extension from original filename if provided
  let extension = 'jpg'; // default
  if (originalFileName) {
    const parts = originalFileName.split('.');
    if (parts.length > 1) {
      extension = parts[parts.length - 1];
    }
  }

  // Format: service-name-attempt-N.ext
  // Example: "day-to-dusk-attempt-2.jpg"
  return `${serviceName}-attempt-${attemptNumber}.${extension}`;
}

/**
 * Download order result with auto-generated filename
 *
 * @param imageUrl - Result image URL
 * @param serviceType - Service type
 * @param attemptNumber - Attempt number
 * @param originalFileName - Original filename (optional)
 */
export async function downloadOrderResult(
  imageUrl: string,
  serviceType: ServiceType,
  attemptNumber: number,
  originalFileName?: string
): Promise<void> {
  const fileName = generateDownloadFileName(serviceType, attemptNumber, originalFileName);
  await downloadImage(imageUrl, fileName);
}
